import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "@patternfly/patternfly/patternfly.css";
import "@patternfly/react-core/dist/styles/base.css";
import { FlagProvider } from "@unleash/proxy-client-react";
import type { IConfig } from "@unleash/proxy-client-react";

/*
  Note: This was intentionally done as a non-blocking dynamic import in order
  to improve initial bundle size. At the time of writing, doing this shaves
  ~70KB off the initial bundle size.
*/
void import("./i18n/config");

import "./index.css";
import { setupStore } from "./app/store";
import routes from "./routes";
import ToastAlertProvider from "./app/components/providers/ToastAlertProvider";
import { toTemporalInstant } from "@js-temporal/polyfill";

declare global {
  interface Date {
    toTemporalInstant: typeof toTemporalInstant;
  }
}

Date.prototype.toTemporalInstant = toTemporalInstant;

const { VITE_UNLEASH_CLIENT_KEY: UNLEASH_CLIENT_KEY } = import.meta.env;
const config: IConfig = {
  url: new URL(`${import.meta.env.BASE_URL}/unleash/proxy`, window.origin),
  clientKey: UNLEASH_CLIENT_KEY,
  refreshInterval: 15,
  appName: "ies-external-user-ui",
};
const store = setupStore();

const ID = "root";
const container = document.getElementById(ID);
if (container === null) {
  throw new Error(`No element with ID ${ID} found`);
}
const root = createRoot(container);
const router = createBrowserRouter(routes);

root.render(
  <React.StrictMode>
    <FlagProvider config={config}>
      <Provider store={store}>
        <ToastAlertProvider>
          <RouterProvider router={router} />
        </ToastAlertProvider>
      </Provider>
    </FlagProvider>
  </React.StrictMode>,
);
